import {
  Alert,
  Box,
  Grid,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { useMemo } from "react";
import { useAppSelector } from "../../store/hooks";
import type { EncounterDrgData } from "../../types/encounter-drg";
import CollapsibleTeamSection from "./CollapsibleTeamSection";
import FinalDrgCard from "./FinalDrgCard";
import QueriesSection from "./QueriesSection";
import TeamCodesSection from "./TeamCodesSection";
import TimelinePanel from "./TimelinePanel";
import { FONT, TW } from "./theme";
import { useEncounterDrg } from "../../hooks/queries";

/* ─── Loading Skeleton ─── */

const EncounterDrgLoading = () => (
  <Stack gap="md">
    <Grid>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Skeleton height={400} radius="md" />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Skeleton height={400} radius="md" />
      </Grid.Col>
    </Grid>
  </Stack>
);

/* ─── Main Component ─── */

const EncounterDRG = () => {
  const mrn = useAppSelector((state) => state.fhir.mrn);
  // const visitId = useAppSelector((state) => state.fhir.encounter);

  const params = useMemo(() => {
    const next: Record<string, string> = { mrn_no: mrn ?? "" };
    // if (visitId) {
    //   next.visit_id = visitId;
    // }
    return next;
  }, [mrn]);

  // When visit_id is required again, add visitId to the dependency array above.

  const {
    data: response,
    isLoading: loading,
    error: fetchError,
  } = useEncounterDrg(params, Boolean(mrn));

  const encounterDrg: EncounterDrgData | null =
    response?.data?.data ?? null;

  const error = useMemo(() => {
    if (!fetchError) return null;
    const status = (fetchError as any)?.response?.status;
    if (status === 404) return null;
    return (
      (fetchError as Error)?.message ||
      "Failed to load encounter DRG details. Please try again."
    );
  }, [fetchError]);

  const codingDrg = encounterDrg?.drgComparison?.coding || null;

  const cdiCodesArr = useMemo(
    () => encounterDrg?.finalization?.cdi?.acceptedCodes || [],
    [encounterDrg?.finalization?.cdi?.acceptedCodes],
  );
  const codingCodesArr = useMemo(
    () => encounterDrg?.finalization?.coding?.acceptedCodes || [],
    [encounterDrg?.finalization?.coding?.acceptedCodes],
  );

  // Codes added by CODING (in coding but not CDI)
  const codingAddedCodes = useMemo(() => {
    const cdiSet = new Set(cdiCodesArr);
    return new Set(codingCodesArr.filter((c) => !cdiSet.has(c)));
  }, [cdiCodesArr, codingCodesArr]);

  // Codes removed by CODING (in CDI but not coding)
  const codingRemovedCodes = useMemo(() => {
    const codingSet = new Set(codingCodesArr);
    return cdiCodesArr.filter((c) => !codingSet.has(c));
  }, [cdiCodesArr, codingCodesArr]);

  const cdiCodeDefs = useMemo(
    () => encounterDrg?.finalization?.cdi?.codeDefinitions || {},
    [encounterDrg?.finalization?.cdi?.codeDefinitions],
  );
  const codingCodeDefs = useMemo(
    () => encounterDrg?.finalization?.coding?.codeDefinitions || {},
    [encounterDrg?.finalization?.coding?.codeDefinitions],
  );
  const allCodeDefinitions = useMemo(() => {
    const defs: Record<string, string> = {};
    if (cdiCodeDefs) Object.assign(defs, cdiCodeDefs);
    if (codingCodeDefs) Object.assign(defs, codingCodeDefs);
    return defs;
  }, [cdiCodeDefs, codingCodeDefs]);

  // Split queries by source
  const cdiQueries = useMemo(
    () => (encounterDrg?.codingQueries || []).filter((q) => q.source === "CDI"),
    [encounterDrg?.codingQueries],
  );
  const codingQueries = useMemo(
    () => (encounterDrg?.codingQueries || []).filter((q) => q.source !== "CDI"),
    [encounterDrg?.codingQueries],
  );

  const cdiPdx = encounterDrg?.finalization?.cdi?.pdx || "";
  const codingPdx = encounterDrg?.finalization?.coding?.pdx || "";

  const missingContext = !mrn;

  return (
    <Box
      p="sm"
      style={{
        height: "100vh",
        overflow: "hidden",
        background: TW.gray50,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Alerts */}
      {missingContext && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          variant="light"
          mb="sm"
        >
          Encounter Summary requires MRN. The current launch context is missing
          MRN.
          {/* When visit_id is required: {!mrn && !visitId ? "both values." : !mrn ? "MRN." : "Visit ID."} */}
        </Alert>
      )}

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          mb="sm"
        >
          {error}
        </Alert>
      )}

      {loading && <EncounterDrgLoading />}

      {!loading && !missingContext && !error && !encounterDrg && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          No data available
        </Alert>
      )}

      {/* Main Content */}
      {!loading && encounterDrg && (
        <div style={{ display: "flex", flex: 1, overflow: "hidden", gap: 0 }}>
          {/* Left Panel */}
          <div style={{ width: "50%" }}>
            <ScrollArea.Autosize mah="100%">
              <Stack gap="sm" pr="xs">
                {/* Date of Service & Final DRG */}
                <Box>
                  <Group gap="md" mb="xs">
                    <Text size={`${FONT.sm}px`} fw={600} c={TW.gray700}>
                      Date of Service
                    </Text>
                    <Text size={`${FONT.sm}px`} fw={500} c={TW.gray600}>
                      {encounterDrg.dos
                        ? new Date(encounterDrg.dos).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )
                        : "N/A"}
                    </Text>
                  </Group>

                  {codingDrg && <FinalDrgCard drg={codingDrg} />}
                </Box>

                {/* CDI Team Section */}
                <CollapsibleTeamSection
                  title="CDI Team"
                  titleColor={TW.fuchsia700}
                  accentColor={TW.fuchsia500}
                  defaultOpened={false}
                >
                  <Stack gap="sm">
                    <TeamCodesSection
                      codes={cdiCodesArr}
                      codeDefinitions={cdiCodeDefs}
                      pdx={cdiPdx}
                    />
                    <QueriesSection queries={cdiQueries} teamLabel="CDI" />
                  </Stack>
                </CollapsibleTeamSection>

                {/* CODING Team Section */}
                <CollapsibleTeamSection
                  title="CODING Team"
                  titleColor={TW.yellow700}
                  accentColor={TW.yellow500}
                >
                  <Stack gap="sm">
                    <TeamCodesSection
                      codes={codingCodesArr}
                      codeDefinitions={codingCodeDefs}
                      pdx={codingPdx}
                      addedCodes={codingAddedCodes}
                      removedCodes={codingRemovedCodes}
                      removedCodeDefinitions={allCodeDefinitions}
                    />
                    <QueriesSection
                      queries={codingQueries}
                      teamLabel="CODING"
                    />
                  </Stack>
                </CollapsibleTeamSection>
              </Stack>
            </ScrollArea.Autosize>
          </div>

          {/* Right Panel: Timeline */}
          <div
            style={{
              width: "50%",
              borderLeft: `1px solid ${TW.gray200}`,
              paddingLeft: 16,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TimelinePanel
              events={encounterDrg.timeline || []}
              caseCreatedByEmail={encounterDrg.caseCreatedByEmail}
            />
          </div>
        </div>
      )}
    </Box>
  );
};

export default EncounterDRG;
